import React from 'react';
import { ButtonPrimary, ButtonSecondary, Modal, ModalBody, ModalFooter, ModalHeader } from '@americanexpress/dls-react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import MonthlyAverageBox from 'src/components/atomic/molecules/monthlyAverageBox/MonthlyAverageBox';
import { useSpendingHistory } from 'src/hooks/queries/spending';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { LocalDate } from '@js-joda/core';
import FormatService from 'src/services/format.service';
import styles from './TargetMonthlySpendingModal.module.scss';
import { useBudget, useDeleteBudget, useUpdateBudget } from 'src/hooks/queries/budgets';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import Selectors from 'src/utils/selectors';
import { DLSSize } from 'src/types/dls-size.enum';
import { getCategoryTransactionsUrl, goToBudgetCategoryTransactions } from 'src/services/router.service';
import { useHistory } from 'react-router';
import DateUtils from 'src/utils/date';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';

export default function TargetMonthlySpendingModal({ closeModal }: { closeModal: () => void }): React.ReactElement {
    const { isLoading: isLoadingTargetMonthlySpending, data: targetMonthlySpending } = useBudget(BUDGET_OVERALL_ID);
    const { mutateAsync: updateBudget } = useUpdateBudget();
    const { mutateAsync: deleteBudget } = useDeleteBudget();
    const startDate: LocalDate = LocalDate.now().minusYears(1).withDayOfMonth(1);
    const endDate: LocalDate = DateUtils.startDateOfThisMonth();
    const { isLoading: isLoadingSpendingHistory, data: spendingHistory } = useSpendingHistory({
        id: BUDGET_OVERALL_ID,
        startDate,
        endDate
    });
    const history = useHistory();

    const handleUpdateBudget = async (values: { monthlyAmount: number }): Promise<void> => {
        await updateBudget({ ...targetMonthlySpending, monthlyAmount: values.monthlyAmount });
        closeModal();
    };

    const handleDeleteBudget = async (): Promise<void> => {
        trackClick(history.location.pathname, adobeTags.CurrentMonth.SetTargetModal.Delete);
        if (targetMonthlySpending.monthlyAmount) await deleteBudget(targetMonthlySpending);
        closeModal();
    };

    if (isLoadingTargetMonthlySpending) return null;

    return (
        <div className={styles.formWrapper}>
            <Formik
                initialValues={{ monthlyAmount: targetMonthlySpending.monthlyAmount }}
                validationSchema={Yup.object({
                    monthlyAmount: Yup.number()
                        .required(LiteralsService.getLiteral('FormErrors.amountRequired'))
                        .moreThan(0, LiteralsService.getLiteral('FormErrors.amountGreaterThanZero'))
                        .lessThan(1000000, LiteralsService.getLiteral('FormErrors.amountLessThanMillion'))
                })}
                onSubmit={handleUpdateBudget}
                enableReinitialize
            >
                {({ isSubmitting }) => (
                    <Form>
                        <Modal
                            aria-labelledby="targetMonthlySpendingModalHeader"
                            id="targetMonthlySpendingModal"
                            className={styles.modal}
                            data-cy={Selectors.SpendSmart.TARGET_MONTHLY_SPENDING_MODAL}
                        >
                            <ModalHeader
                                id="targetMonthlySpendingModalHeader"
                                onClose={() => {
                                    trackClick(history.location.pathname, adobeTags.CurrentMonth.SetTargetModal.Cancel);
                                    closeModal();
                                }}
                                data-cy={Selectors.SpendSmart.TARGET_MONTHLY_SPENDING_MODAL_HEADER}
                            >
                                <Typography
                                    component="h1"
                                    variant={TypographyVariants.HEADING_XS}
                                    className={styles.modalHeader}
                                >
                                    {LiteralsService.getLiteral('TargetMonthlySpending.modalHeader')}
                                </Typography>
                            </ModalHeader>
                            <ModalBody className={styles.modalBody}>
                                <Typography variant={TypographyVariants.BODY}>
                                    {LiteralsService.getLiteral('TargetMonthlySpending.modalBody')}
                                </Typography>
                                <div className={styles.targetMonthlySpendingContainer}>
                                    <CurrencyInput
                                        id="monthlyAmount"
                                        label={LiteralsService.getLiteral('TargetMonthlySpending.title')}
                                        data-cy={Selectors.SpendSmart.TARGET_MONTHLY_SPENDING_INPUT}
                                        className={styles.targetMonthAmountInput}
                                        tagName={adobeTags.CurrentMonth.SetTargetModal.TargetAmount}
                                    />
                                    <MonthlyAverageBox
                                        amount={FormatService.format(spendingHistory?.[0].averageSpent)}
                                        id="targetMonthlySpendingModalTooltip"
                                        onClick={() => {
                                            goToBudgetCategoryTransactions(
                                                false,
                                                history,
                                                BUDGET_OVERALL_ID,
                                                startDate,
                                                endDate
                                            );
                                        }}
                                        tag={adobeTags.CurrentMonth.SetTargetModal.ViewTransactions}
                                        href={getCategoryTransactionsUrl(BUDGET_OVERALL_ID, startDate, endDate)}
                                        className={styles.targetMonthlySpendingMonthlyAverageBox}
                                        subtext={
                                            <Typography
                                                variant={TypographyVariants.SUBTEXT}
                                                component="p"
                                                aria-live="polite"
                                                data-cy={
                                                    Selectors.SpendSmart.TARGET_MONTHLY_SPENDING_MONTHLY_AVERAGE_SUBTEXT
                                                }
                                            >
                                                {LiteralsService.getLiteral('Budget.Create.monthlyAverageAllAccounts')}
                                            </Typography>
                                        }
                                        linkText={LiteralsService.getLiteral('TargetMonthlySpending.viewTransactions')}
                                        tooltipClassName={styles.monthlyAverageBoxTooltipButton}
                                        loadingAmount={isLoadingSpendingHistory}
                                        tagName={adobeTags.CurrentMonth.SetTargetModal.Tooltip}
                                    />
                                </div>
                            </ModalBody>
                            <ModalFooter className={styles.modalFooter}>
                                <ButtonSecondary
                                    className={styles.bottomButton}
                                    onClick={handleDeleteBudget}
                                    size={DLSSize.MEDIUM}
                                    data-cy={Selectors.SpendSmart.DELETE_BUTTON}
                                >
                                    {LiteralsService.getLiteral('CommonText.delete')}
                                </ButtonSecondary>
                                <ButtonPrimary
                                    className={styles.bottomButton}
                                    type="submit"
                                    loading={isSubmitting}
                                    onClick={() =>
                                        trackClick(
                                            history.location.pathname,
                                            adobeTags.CurrentMonth.SetTargetModal.Confirm
                                        )
                                    }
                                    data-cy={Selectors.SpendSmart.CONFIRM_BUTTON}
                                    size={DLSSize.MEDIUM}
                                >
                                    {LiteralsService.getLiteral('CommonText.confirm')}
                                </ButtonPrimary>
                            </ModalFooter>
                        </Modal>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
